import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown } from 'react-icons/fi';

const FAQSection = styled.section`
  background: #fff;
  padding: 100px 20px;
  width: 100%;
  direction: rtl;
`;

const Inner = styled.div`max-width: 740px; margin: 0 auto;`;

const Eyebrow = styled.div`
  font-size: 0.8rem; font-weight: 700;
  color: #DA015C; margin-bottom: 16px; text-align: center;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 2.8rem); font-weight: 900;
  color: #0a174e; text-align: center;
  letter-spacing: -0.02em; line-height: 1.1; margin-bottom: 12px;
  span { color: #DA015C; }
`;

const Subtitle = styled.p`
  font-size: 1.05rem; color: #5a6a7c;
  text-align: center; line-height: 1.75; margin-bottom: 56px;
`;

const FAQList = styled.div`display: flex; flex-direction: column; gap: 12px;`;

const FAQItem = styled.div<{ open: boolean }>`
  background: ${p => p.open ? '#0a174e' : '#f8fafb'};
  border-radius: 16px;
  border: 1px solid ${p => p.open ? 'transparent' : 'rgba(10,23,78,0.06)'};
  overflow: hidden;
  transition: background 0.3s ease, box-shadow 0.3s ease;
  box-shadow: ${p => p.open ? '0 8px 32px rgba(10,23,78,0.18)' : '0 2px 8px rgba(10,23,78,0.03)'};
`;

const FAQQuestion = styled.button<{ open: boolean }>`
  width: 100%; background: none; border: none; outline: none;
  font-size: 1.05rem; font-weight: 700;
  color: ${p => p.open ? '#fff' : '#0a174e'};
  padding: 22px 24px; text-align: right;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  cursor: pointer; transition: color 0.25s;
`;

const ChevronIcon = styled(motion.span)<{ open: boolean }>`
  flex-shrink: 0; display: flex;
  color: ${p => p.open ? '#DA015C' : '#5a6a7c'}; font-size: 1.2rem;
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 24px 22px 24px; font-size: 0.98rem;
  color: rgba(255,255,255,0.75); line-height: 1.8; overflow: hidden;
`;

const faqs = [
  { q: "איך נרשמים כנוסע?", a: "פשוט מאוד — הצטרף לרשימת ההמתנה למעלה עם מספר הטלפון והעיר שלך. נשלח לך הזמנה ברגע שוואסל תגיע לאזורך." },
  { q: "לאילו ערים וואסל מגיעה?", a: "אנחנו מתחילים בערים שהוזנחו על ידי פלטפורמות ההסעות הגלובליות — בישראל, ובמדינות נוספות ברחבי העולם. עוד ערים מצטרפות כל חודש. הירשם ותגיד לנו את העיר שלך!" },
  { q: "כמה עולה נסיעה?", a: "וואסל בנויה על תמחור הוגן ושקוף. תמיד תראה את המחיר המלא לפני אישור הנסיעה — ללא תמחור דינמי, ללא עמלות נסתרות." },
  { q: "האם וואסל בטוחה?", a: "הבטיחות היא הדבר הראשון שחשבנו עליו. כל הנהגים עוברים בדיקת רקע ואימות. כל נסיעה עם מעקב GPS, ואפשר לשתף את מסלול הנסיעה עם בני המשפחה." },
  { q: "אפשר להזמין נסיעה עבור מישהו אחר?", a: "בהחלט. ניתן להזמין נסיעה עבור בני משפחה, חברים או עמיתים ישירות מהחשבון שלך — גם אם אין להם את האפליקציה." },
  { q: "מה מייחד את וואסל מאפליקציות אחרות?", a: "וואסל נבנתה במיוחד עבור קהילות שנדחקו לשוליים על ידי הפלטפורמות הגלובליות. אנחנו מעדיפים צרכים מקומיים, הכנסה הוגנת לנהגים ואמון אמיתי — לא רק מספרי צמיחה." },
];

const DriverFAQHe: React.FC = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <FAQSection id="faq">
      <Inner>
        <Eyebrow>שאלות נפוצות</Eyebrow>
        <Title>כל מה שרצית <span>לדעת</span></Title>
        <Subtitle>כל מה שצריך לדעת לפני הנסיעה הראשונה שלך עם וואסל.</Subtitle>
        <FAQList>
          {faqs.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <FAQItem key={idx} open={isOpen}>
                <FAQQuestion open={isOpen} onClick={() => setOpenIdx(isOpen ? null : idx)} aria-expanded={isOpen}>
                  {item.q}
                  <ChevronIcon open={isOpen} animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                    <FiChevronDown />
                  </ChevronIcon>
                </FAQQuestion>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <FAQAnswer key="a" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }}>
                      {item.a}
                    </FAQAnswer>
                  )}
                </AnimatePresence>
              </FAQItem>
            );
          })}
        </FAQList>
      </Inner>
    </FAQSection>
  );
};

export default DriverFAQHe;
